import {Navigate, Outlet, useLocation} from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useSociety from "../../hooks/useSociety";
import {useSectionsRefs} from "../mainWrapper/MainWrapper";
import {useEffect, useState} from "react";
import {jwtDecode} from "jwt-decode";
import useRefreshToken from "../../hooks/useRefreshToken";

interface RequireAuthProps {
    isSocietyRelevant?: boolean,
}

const RequireAuth = ({isSocietyRelevant = true}: RequireAuthProps) => {
    const {auth, setAuth} = useAuth();
    const location = useLocation();
    const {society} = useSociety();
    const refresh = useRefreshToken();
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const {setNavItems, setDisplayRegisterSocietyButton, setDisplayCart} = useSectionsRefs();

    useEffect(() => {
        let isMounted = true;

        const verifyTokenAndPotentiallyRefresh = async () => {
            setIsLoading(true);
            try {
                if (!auth?.token) {
                    setIsAuthenticated(false);
                    return;
                }

                const decodedToken = jwtDecode(auth.token) as { exp: number };
                const currentTime = Math.floor(Date.now() / 1000);

                if (decodedToken.exp > currentTime) {
                    // Token is still valid
                    setIsAuthenticated(true);
                } else {
                    // Token has expired, attempt to refresh
                    const newToken = await refresh();
                    if (isMounted && newToken) {
                        setIsAuthenticated(true);
                    } else {
                        setIsAuthenticated(false);
                    }
                }
            } catch (error) {
                console.error("Error verifying or refreshing token:", error);
                setIsAuthenticated(false);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        verifyTokenAndPotentiallyRefresh();

        return () => {
            isMounted = false;
        };
    }, [auth?.token, refresh]);

    if (isLoading) {
        return <p>Loading...</p>; // Or your preferred loading indicator
    }

    return (
        (isAuthenticated && (!isSocietyRelevant || auth?.societyId === society.id))
            ? <Outlet context={{setNavItems, setDisplayRegisterSocietyButton, setDisplayCart}}/>
            : <Navigate to="/unauthorized" state={{from: location}} replace/>
    )
}

export default RequireAuth;